/**
 * Bereich D des Zielbilds vom 11.08.2026: AUS DEM RADAR.
 *
 * Bisher verschwand ein Wert lautlos aus der Liste. Man merkte es erst, wenn
 * man ihn suchte — und dann fehlte die Antwort, WARUM. Ein Signal, das
 * ausläuft, ist aber ein Ergebnis; eines, das niemand sieht, lehrt niemanden
 * etwas.
 *
 * Die Karte liest zwei Zeilen derselben Tagesakte — die von vorher und die von
 * jetzt — und nennt den Wechsel samt Begründung des Wechseltages. Sie rechnet
 * nichts nach; ohne Akte gibt es hier schlicht nichts zu sehen.
 *
 * BEWUSST NUR SIEBEN TAGE. Danach ist ein Abgang keine Neuigkeit mehr,
 * sondern Geschichte — und die steht im Signal-Verlauf auf /signale.
 */
import { Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';

import { useSnapshots } from '@/hooks/use-snapshots';
import type { RadarAbgang } from '@/lib/v6/snapshot-read';
import { CARD, CARD_HINT, EMPTY_STATE } from '@/lib/design-tokens';
import { useDemoMode } from '@/contexts/DemoModeContext';

const MAX_ZEILEN = 6;

/** Der Zustand in Alltagssprache — die Datenbankwerte gehören nicht auf die Seite. */
const ZUSTAND_TEXT: Record<string, string> = {
  kein_setup: 'kein Setup mehr',
  boden_reift: 'Boden reift',
  zone_offen: 'Kaufzone offen',
  zone_verlassen: 'Kaufzone abgelaufen',
  ungueltig: 'These widerlegt',
  daten_unzureichend: 'zu wenig Kursdaten',
};

function zustandText(z: string): string {
  return ZUSTAND_TEXT[z] ?? z;
}

export function AusDemRadarCard() {
  const { abgaenge, isLoading, leer } = useSnapshots();
  const { linkTo } = useDemoMode();

  const sichtbar = abgaenge.slice(0, MAX_ZEILEN);
  const weitere = abgaenge.length - sichtbar.length;

  return (
    <div className={CARD}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
          <LogOut className="size-4 text-zinc-400" />
          Aus dem Radar
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">letzte 7 Tage</span>
      </div>
      <p className={`${CARD_HINT} mb-3`}>
        Werte, die zuletzt beachtenswert waren und es nicht mehr sind — mit dem
        Grund vom Tag des Wechsels.
      </p>

      {isLoading && <div className="skeleton h-12 w-full" />}

      {!isLoading && leer && (
        <div className={EMPTY_STATE}>
          Noch keine Tagesakte — ohne zwei Tage zum Vergleichen gibt es keinen
          Wechsel zu zeigen.
        </div>
      )}

      {!isLoading && !leer && abgaenge.length === 0 && (
        <div className={EMPTY_STATE}>
          In den letzten sieben Tagen ist nichts herausgefallen.
        </div>
      )}

      {sichtbar.length > 0 && (
        <ul className="space-y-1.5">
          {sichtbar.map((a) => (
            <li key={`${a.symbol}-${a.seit}`}>
              <Link
                to={linkTo(`/symbol/swing/${encodeURIComponent(a.symbol)}`)}
                className="block rounded border border-zinc-800/70 bg-zinc-900/40 px-2.5 py-1.5 hover:bg-zinc-800/40 transition-colors focus-ring"
              >
                <AbgangZeile abgang={a} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {weitere > 0 && (
        <div className="mt-2 text-[10px] text-zinc-500">{weitere} weitere.</div>
      )}
    </div>
  );
}

function AbgangZeile({ abgang }: { abgang: RadarAbgang }) {
  // Nur der erste Grund: Die Zustandsmaschine nennt oft mehrere, aber der
  // erste ist der, der den Wechsel ausgelöst hat.
  const grund = abgang.gruende[0] ?? null;
  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-sm font-semibold text-zinc-200">{abgang.symbol}</span>
        <span className="text-[10px] text-zinc-500">seit {abgang.seit}</span>
      </div>
      <div className="text-[11px] text-zinc-400 leading-snug">
        {zustandText(abgang.von)} <span className="text-zinc-600">→</span>{' '}
        {zustandText(abgang.nach)}
        {grund && <span className="text-zinc-500"> · {grund}</span>}
      </div>
    </>
  );
}
